import React, { Fragment, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './Appointments.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setPopUp } from 'actions/environment';
import AppointmentInfoDialog from './AppointmentDialog/AppointmentInfoDialog';
import { loadAppointmentStatuses } from 'actions/appointment';
import * as types from 'constants/ActionTypes.js';
import { ButtonSpinner } from '../../common/ButtonSpinner/ButtonSpinner';
import { gaEvent, trackGA } from '../../../services/ga';
import AppointmentCard from './AppointmentCard/AppointmentCard';

export const getStatusLabel = (status, t) => {
  let statusClass;
  let statusText;

  switch (status) {
    case 0:
      statusClass = styles.inProgress;
      statusText = t('components.profile.appointments.inProgress');
      break;
    case 1:
      statusClass = styles.assigned;
      statusText = t('components.profile.appointments.appointed');
      break;
    case 2:
      statusClass = styles.completed;
      statusText = t('components.profile.appointments.completed');
      break;
    case 3:
      statusClass = styles.canceled;
      statusText = t('components.profile.appointments.canceled');
      break;
    default:
      break;
  }

  return <div className={`${styles.StatusLabel} ${statusClass}`}>{statusText}</div>;
};

export const getTypeLabel = (type, t) => {
  const typeClass = type === 'online' ? styles.online : styles.offline;
  const typeText =
    type === 'online'
      ? t('components.profile.appointments.online')
      : t('components.profile.appointments.offline');

  return <div className={`${styles.StatusLabel} ${typeClass}`}>{typeText}</div>;
};

export const formatAppointmentDate = (dateString, t, language = 'ru-RU') => {
  const appointmentDate = new Date(dateString);
  const now = new Date();

  const isToday = appointmentDate.toDateString() === now.toDateString();
  const isYesterday =
    appointmentDate.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString();

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  if (isToday) {
    return `${t('components.profile.appointments.today')} ${appointmentDate.toLocaleTimeString(
      language,
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    )}`;
  }

  if (isYesterday) {
    return `${t('components.profile.appointments.yesterday')} ${appointmentDate.toLocaleTimeString(
      language,
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    )}`;
  }

  return `${appointmentDate.toLocaleDateString(language, { ...options })}`;
};

const Appointments = ({ profile }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeTabIndex, setActiveTabIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [futureAppointments, setFutureAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await dispatch(loadAppointmentStatuses());
        if (result.data) {
          const future = result.data.items.filter(
            (appointment) => appointment.status === 0 || appointment.status === 1
          );
          const past = result.data.items.filter(
            (appointment) => appointment.status === 2 || appointment.status === 3
          );

          setFutureAppointments(future);
          setPastAppointments(past);
        } else {
          dispatch({ type: types.LOAD_APPOINTMENT_STATUSES_ERROR });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  const handleSwitchTab = (tabIndex) => {
    const eventLabel = tabIndex === 1 ? 'budushchie' : 'proshedshie';

    trackGA(
      gaEvent.profileAppointmentsTabsClick({
        eventLabel,
        user_id: profile.id,
      })
    );

    if (activeTabIndex !== tabIndex) {
      setActiveTabIndex(tabIndex);
    }
  };

  const handleShowInfo = (appointment) => {
    dispatch(
      setPopUp(<AppointmentInfoDialog appointment={appointment} />, {
        closeStyleName: 'Default',
        contentStyleName: 'Default',
        closeIcon: 'default',
        lockScroll: false,
      })
    );
  };

  return (
    <div className={styles.Appointments}>
      {isLoading ? (
        <ButtonSpinner />
      ) : (
        <Fragment>
          <div className={styles.TabButtons}>
            <button
              className={cn(styles.TabButton, { [styles.Active]: activeTabIndex === 1 })}
              onClick={() => handleSwitchTab(1)}
            >
              {t('components.profile.appointments.futureAppointments')}
            </button>
            <button
              className={cn(styles.TabButton, { [styles.Active]: activeTabIndex === 2 })}
              onClick={() => handleSwitchTab(2)}
            >
              {t('components.profile.appointments.pastAppointments')}
            </button>
          </div>

          <div className={styles.AppointmentCards}>
            {activeTabIndex === 1 && futureAppointments.length > 0 ? (
              futureAppointments.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  activeTabIndex={activeTabIndex}
                  profileId={profile.id}
                  onShowInfo={handleShowInfo}
                />
              ))
            ) : activeTabIndex === 1 ? (
              <div className={styles.NoAppointments}>
                <p className={styles.NoAppointmentsTitle}>
                  {t('components.profile.appointments.noAppointments')}
                </p>
                <p className={styles.NoAppointmentsDescription}>
                  {t('components.profile.appointments.noFutureAppointments')}
                </p>
              </div>
            ) : null}

            {activeTabIndex === 2 && pastAppointments.length > 0 ? (
              pastAppointments.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  activeTabIndex={activeTabIndex}
                  profileId={profile.id}
                  onShowInfo={handleShowInfo}
                />
              ))
            ) : activeTabIndex === 2 ? (
              <div className={styles.NoAppointments}>
                <p className={styles.NoAppointmentsTitle}>
                  {t('components.profile.appointments.noAppointments')}
                </p>
                <p className={styles.NoAppointmentsDescription}>
                  {t('components.profile.appointments.noPastAppointments')}
                </p>
              </div>
            ) : null}
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default Appointments;



// WEBPACK FOOTER //
// ./src/components/Profile/Appointments/Appointments.jsx